/**
 * RFIAdjustmentTable — admin-only editable Daily Model Recommendation table.
 *
 * Renders ONE row per `daily_analysis_rows` entry that has both an actual feed
 * AND a recommended feed (rows without one of those are not RFI-relevant).
 * The Adjusted-Recommended input is the only editable column; everything else
 * is computed live from the hook's SSOT injector.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, RotateCcw, RefreshCw, Save, Upload, Copy, Lock, AlertTriangle, X, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import ColumnFilterHeader, { useColumnFilters } from './ColumnFilterHeader';

// Classification thresholds — mirror the existing RFI banner in
// SpreadsheetGrowthComparison.js so narrative + this table never disagree.
const classifyRfi = (rfi) => {
  if (rfi == null || !Number.isFinite(rfi)) return { label: '—', tone: 'slate' };
  if (rfi > 110) return { label: 'Overfeeding',  tone: 'red' };
  if (rfi > 100) return { label: 'Slight Over',  tone: 'amber' };
  if (rfi >= 90) return { label: 'Optimal',       tone: 'green' };
  if (rfi >= 75) return { label: 'Underfeeding', tone: 'blue' };
  return                  { label: 'Restricted',   tone: 'indigo' };
};

const toneClass = (tone) => ({
  red:    'text-red-300 bg-red-500/15  border-red-500/40',
  amber:  'text-amber-200 bg-amber-500/15 border-amber-500/40',
  green:  'text-green-300 bg-green-500/15 border-green-500/40',
  blue:   'text-blue-300 bg-blue-500/15 border-blue-500/40',
  indigo: 'text-indigo-300 bg-indigo-500/15 border-indigo-500/40',
  slate:  'text-slate-300 bg-slate-500/15 border-slate-500/40',
}[tone] || 'text-slate-300 bg-slate-500/15 border-slate-500/40');

const fmtKg = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(2));
const fmtPct = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `${Number(v).toFixed(1)}%`);

export default function RFIAdjustmentTable({ rfiScenario, processedDailyRows }) {
  const {
    enabled, setEnabled,
    showOverlay, setShowOverlay,
    overrideCount, isOverridden, isLocked,
    scenarioId, scenarioName, setScenarioName, scenarioStatus, savedScenarios,
    setOverride, resetRow, resetAll, applyToRemaining,
    saveScenario, loadScenario, cloneScenario, publishScenario, archiveScenario, newScenario,
    refreshScenarioList, fetchAudit, loading,
  } = rfiScenario;

  const [expanded, setExpanded] = useState(true);
  const [bulkValue, setBulkValue] = useState('');
  const [bulkMode, setBulkMode] = useState('percent'); // percent | delta
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [resetAllConfirmOpen, setResetAllConfirmOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);

  // Eligible rows: have both actual feed and an original recommended feed
  // to anchor the adjustment on. Recompute against the LIVE processed rows
  // so daily/cumulative RFI displayed here is always SSOT-consistent.
  const tableRows = useMemo(() => {
    if (!Array.isArray(processedDailyRows)) return [];
    const filtered = processedDailyRows.filter(
      (r) => r && r.date && (r.daily_feed_kg != null || r.recommended_feed_kg != null),
    );
    let cumActual = 0;
    let cumAdj = 0;
    let cumOrig = 0;
    return filtered.map((r) => {
      const actual = Number(r.daily_feed_kg || 0);
      const adjusted = Number(r.recommended_feed_kg || 0);
      const original = Number(r.original_recommended_feed_kg ?? r.recommended_feed_kg ?? 0);
      const fed = actual > 0;
      if (fed) {
        cumActual += actual;
        if (adjusted > 0) cumAdj += adjusted;
        if (original > 0) cumOrig += original;
      }
      const dailyRfi = fed && adjusted > 0 ? (actual / adjusted) * 100 : null;
      const cumRfi = cumAdj > 0 ? (cumActual / cumAdj) * 100 : null;
      const correctedRfi = cumOrig > 0 ? (cumActual / cumOrig) * 100 : null;
      return {
        date: r.date,
        actual,
        original,
        adjusted,
        dailyRfi,
        cumRfi,
        correctedRfi,
        sample_weight_g: r.actual_weight_g != null ? Number(r.actual_weight_g) : null,
        biomass_kg: r.actual_biomass_kg != null ? Number(r.actual_biomass_kg) : null,
        daily_mortality: r.daily_mortality != null ? Number(r.daily_mortality) : null,
        missing_fish: r.daily_missing_fish_estimate != null ? Number(r.daily_missing_fish_estimate) : null,
        temperature: r.temperature != null ? Number(r.temperature) : null,
        fed,
        edited: isOverridden(r.date),
        klass: classifyRfi(fed && adjusted > 0 ? (actual / adjusted) * 100 : null).label,
      };
    });
  }, [processedDailyRows, isOverridden]);

  // ─── Excel-style per-column sort & filter ────────────────────────────
  // Column definitions with type-aware filters (numeric / text / multi).
  // Visible columns: Date, Actual Feed, Original Rec, Adjusted Rec, Daily RFI,
  // Acc RFI, Corrected RFI, Sample Wt, Biomass, Mortality, Missing Fish, Class.
  // The Adjusted Rec column stays editable but still sortable/filterable.
  const columns = useMemo(() => ([
    { key: 'date',           label: 'Date',          type: 'text',    accessor: (r) => r.date },
    { key: 'temperature',    label: 'Temp (°C)',     type: 'numeric', accessor: (r) => r.temperature },
    { key: 'actual',         label: 'Actual',        type: 'numeric', accessor: (r) => r.actual },
    { key: 'original',       label: 'Orig Rec',      type: 'numeric', accessor: (r) => r.original },
    { key: 'adjusted',       label: 'Adj Rec',       type: 'numeric', accessor: (r) => r.adjusted },
    { key: 'dailyRfi',       label: 'RFI %',         type: 'numeric', accessor: (r) => r.dailyRfi },
    { key: 'cumRfi',         label: 'Acc RFI %',     type: 'numeric', accessor: (r) => r.cumRfi },
    { key: 'correctedRfi',   label: 'Corr RFI %',    type: 'numeric', accessor: (r) => r.correctedRfi },
    { key: 'sample_weight_g',label: 'Wt (g)',        type: 'numeric', accessor: (r) => r.sample_weight_g },
    { key: 'biomass_kg',     label: 'Biomass (kg)',  type: 'numeric', accessor: (r) => r.biomass_kg },
    { key: 'daily_mortality',label: 'Mortality',     type: 'numeric', accessor: (r) => r.daily_mortality },
    { key: 'missing_fish',   label: 'Miss Fish',     type: 'numeric', accessor: (r) => r.missing_fish },
    { key: 'klass',          label: 'Class',         type: 'multi',   accessor: (r) => r.klass },
  ]), []);

  // Persist filter state in sessionStorage so navigating to another page and
  // back keeps the admin's filter setup. Scoped per storageKey so different
  // tables can persist independently.
  const STORAGE_KEY = 'pfd-rfi-table-filters';
  const initialColumnState = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
  }, []);
  const colFilters = useColumnFilters(initialColumnState);
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(colFilters.state)); } catch { /* quota */ }
  }, [colFilters.state]);

  // Apply filters + sort. AND across all column filters; single-column sort.
  const displayRows = useMemo(
    () => colFilters.apply(tableRows, columns),
    [tableRows, columns, colFilters.state, colFilters],
  );

  const unrealisticDailyDates = useMemo(
    () => tableRows
      .filter((r) => r.edited && r.dailyRfi != null && (r.dailyRfi < 30 || r.dailyRfi > 250))
      .map((r) => r.date),
    [tableRows],
  );

  const handleEdit = (row, raw) => {
    const v = Number(raw);
    setOverride(row.date, v, row.original);
  };

  const handleBulkApply = () => {
    const remainingFromDate = tableRows[0]?.date;
    if (!remainingFromDate) return;
    const v = Number(bulkValue);
    if (!Number.isFinite(v)) return;
    const touched = applyToRemaining(remainingFromDate, processedDailyRows, v, bulkMode);
    setBulkConfirmOpen(false);
    setBulkValue('');
  };

  const openAudit = async () => {
    setAuditOpen(true);
    setAuditEntries(await fetchAudit());
  };

  // Header — short summary chip + master switch + scenario controls.
  const summary = (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 px-2 py-1 rounded text-cyan-200 hover:bg-white/5"
        data-testid="rfi-override-collapse-toggle"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span className="text-sm font-semibold">Daily Model Recommendation Adjustment</span>
        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] bg-amber-500/20 text-amber-200 border border-amber-500/40">ADMIN</span>
      </button>
      {overrideCount > 0 && (
        <span className="text-[11px] text-red-200 bg-red-500/15 border border-red-500/40 px-2 py-0.5 rounded">
          {overrideCount} day{overrideCount === 1 ? '' : 's'} adjusted
        </span>
      )}
      {scenarioStatus !== 'draft' && scenarioId && (
        <span className={`text-[11px] px-2 py-0.5 rounded border ${scenarioStatus === 'published' ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200' : 'bg-slate-500/10 border-slate-500/40 text-slate-300'}`}>
          <Lock className="w-3 h-3 inline mr-1" />
          {scenarioStatus}
        </span>
      )}
      <label className="ml-auto flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          data-testid="rfi-override-master-toggle"
          className="h-3.5 w-3.5"
        />
        <span>{enabled ? 'Scenario active' : 'Showing original record'}</span>
      </label>
      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" title="Show original vs adjusted on charts">
        <input
          type="checkbox"
          checked={showOverlay}
          onChange={(e) => setShowOverlay(e.target.checked)}
          data-testid="rfi-override-overlay-toggle"
          className="h-3.5 w-3.5"
        />
        <span>Overlay on charts</span>
      </label>
    </div>
  );

  return (
    <div className="bg-slate-900/60 border border-amber-500/30 rounded-lg p-3 mb-4" data-testid="rfi-override-panel">
      {summary}

      {expanded && (
        <>
          {/* Safety banner — clarifies that scenarios are isolated from the
              underlying spreadsheet record. Even after Save/Publish the
              original imported values remain untouched in the
              `spreadsheet_records` collection. Toggle the master switch off
              to instantly view originals. */}
          <div className="text-[11px] text-emerald-200/90 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1.5 mb-2 flex items-center gap-2" data-testid="rfi-override-safety-banner">
            <span className="font-semibold">Sandbox</span>
            <span>·</span>
            <span>Original imported values are never overwritten. Save / Publish only persists this scenario in a separate collection — toggle <span className="font-medium">Scenario active</span> off above to view the underlying record.</span>
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
            <Input
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder="Scenario name…"
              disabled={isLocked}
              className="h-7 w-44 text-xs bg-white/5 text-slate-100 border-slate-600"
              data-testid="rfi-scenario-name"
            />
            <Button size="sm" className="h-7 text-xs" variant="secondary" disabled={loading || isLocked} onClick={() => saveScenario(scenarioName || 'Untitled scenario')} data-testid="rfi-scenario-save">
              <Save className="w-3 h-3 mr-1" /> Save
            </Button>
            <Button size="sm" className="h-7 text-xs" variant="ghost" disabled={loading} onClick={async () => { await refreshScenarioList(); setLoadDialogOpen(true); }} data-testid="rfi-scenario-load">
              <Upload className="w-3 h-3 mr-1" /> Load
            </Button>
            <Button size="sm" className="h-7 text-xs" variant="ghost" disabled={loading || !scenarioId} onClick={() => cloneScenario(scenarioId)} data-testid="rfi-scenario-clone">
              <Copy className="w-3 h-3 mr-1" /> Clone
            </Button>
            <Button size="sm" className="h-7 text-xs" variant="ghost" disabled={loading || !scenarioId || isLocked} onClick={publishScenario} data-testid="rfi-scenario-publish">
              <Lock className="w-3 h-3 mr-1" /> Publish
            </Button>
            <Button size="sm" className="h-7 text-xs" variant="ghost" disabled={loading || !scenarioId} onClick={archiveScenario} data-testid="rfi-scenario-archive">
              Archive
            </Button>
            <Button size="sm" className="h-7 text-xs" variant="ghost" disabled={loading} onClick={newScenario} data-testid="rfi-scenario-new">
              New
            </Button>
            <Button size="sm" className="h-7 text-xs" variant="ghost" disabled={!scenarioId} onClick={openAudit} data-testid="rfi-scenario-audit-open">
              Audit log
            </Button>
            <div className="flex items-center gap-1 ml-auto">
              <Input
                type="number"
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                placeholder={bulkMode === 'percent' ? '+/- %' : '+/- kg/day'}
                disabled={isLocked}
                className="h-7 w-24 text-xs bg-white/5 text-slate-100 border-slate-600"
                data-testid="rfi-bulk-value"
              />
              <Select value={bulkMode} onValueChange={setBulkMode}>
                <SelectTrigger className="h-7 w-20 text-xs bg-white/5 text-slate-100 border-slate-600" data-testid="rfi-bulk-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">%</SelectItem>
                  <SelectItem value="delta">kg/day</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" className="h-7 text-xs" disabled={isLocked || !bulkValue} onClick={() => setBulkConfirmOpen(true)} data-testid="rfi-bulk-apply">
                Apply to remaining
              </Button>
              <Button size="sm" className="h-7 text-xs" variant="destructive" disabled={isLocked || overrideCount === 0} onClick={() => setResetAllConfirmOpen(true)} data-testid="rfi-reset-all">
                <RefreshCw className="w-3 h-3 mr-1" /> Reset all
              </Button>
            </div>
          </div>

          {/* Unrealistic warning */}
          {unrealisticDailyDates.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-red-200 bg-red-500/10 border border-red-500/40 rounded p-2 mb-2" data-testid="rfi-override-unrealistic">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                {unrealisticDailyDates.length} adjusted day{unrealisticDailyDates.length === 1 ? '' : 's'} produce a biologically unusual Daily RFI (&lt;30% or &gt;250%). Double-check before publishing.
              </span>
            </div>
          )}

          {/* Active-filter summary row */}
          {colFilters.activeCount > 0 && (
            <div className="flex items-center justify-between text-[11px] text-cyan-200 bg-cyan-500/10 border border-cyan-500/30 rounded px-2 py-1 mb-2" data-testid="rfi-colfilters-active">
              <span>
                {colFilters.activeCount} column filter{colFilters.activeCount === 1 ? '' : 's'} active · showing {displayRows.length} of {tableRows.length} rows
              </span>
              <button
                type="button"
                onClick={colFilters.clearAll}
                className="text-cyan-200 hover:text-white underline"
                data-testid="rfi-colfilters-clear-all"
              >
                Clear column filters
              </button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-auto max-h-96 border border-slate-700 rounded">
            <table className="w-full text-xs" data-testid="rfi-override-table">
              <thead className="sticky top-0 bg-slate-800 text-slate-300 z-10">
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`px-2 py-1.5 font-medium text-left whitespace-nowrap ${col.type === 'numeric' ? 'text-right' : 'text-left'}`}
                      data-testid={`rfi-colhead-${col.key}`}
                    >
                      <ColumnFilterHeader
                        col={col}
                        columnState={colFilters.state[col.key]}
                        rows={tableRows}
                        onSort={colFilters.cycleSort}
                        onSetFilter={colFilters.setFilter}
                        onClearFilter={colFilters.clearFilter}
                      />
                    </th>
                  ))}
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row) => {
                  const cls = classifyRfi(row.dailyRfi);
                  const editedTone = row.edited ? 'text-red-300 font-semibold' : 'text-slate-200';
                  return (
                    <tr key={row.date} className={`border-t border-slate-800 ${row.edited ? 'bg-red-500/10' : ''}`} data-testid={`rfi-row-${row.date}`}>
                      <td className="px-2 py-1 text-slate-200 whitespace-nowrap">{row.date}</td>
                      <td className="px-2 py-1 text-right text-slate-300" data-testid={`rfi-temp-${row.date}`}>
                        {row.temperature != null ? row.temperature.toFixed(1) : '—'}
                      </td>
                      <td className={`px-2 py-1 text-right ${row.edited ? editedTone : 'text-slate-300'}`}>{fmtKg(row.actual)}</td>
                      <td className="px-2 py-1 text-right text-slate-400">{fmtKg(row.original)}</td>
                      <td className="px-2 py-1 text-right">
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={row.adjusted}
                          disabled={isLocked}
                          onChange={(e) => handleEdit(row, e.target.value)}
                          className={`h-6 w-20 text-right text-xs ${row.edited ? 'border-red-500 ring-1 ring-red-500/40 bg-red-500/10 text-red-100 font-semibold' : 'bg-white/5 text-slate-100 border-slate-700'}`}
                          data-testid={`rfi-input-${row.date}`}
                        />
                      </td>
                      <td className={`px-2 py-1 text-right ${editedTone}`}>{fmtPct(row.dailyRfi)}</td>
                      <td className={`px-2 py-1 text-right ${editedTone}`}>{fmtPct(row.cumRfi)}</td>
                      <td className="px-2 py-1 text-right text-slate-300">{fmtPct(row.correctedRfi)}</td>
                      <td className="px-2 py-1 text-right text-slate-300">{row.sample_weight_g != null ? row.sample_weight_g.toFixed(1) : '—'}</td>
                      <td className="px-2 py-1 text-right text-slate-300">{row.biomass_kg != null ? row.biomass_kg.toFixed(1) : '—'}</td>
                      <td className="px-2 py-1 text-right text-slate-300">{row.daily_mortality != null ? row.daily_mortality.toFixed(0) : '—'}</td>
                      <td className="px-2 py-1 text-right text-slate-300">{row.missing_fish != null ? row.missing_fish.toFixed(0) : '—'}</td>
                      <td className={`px-2 py-1`}>
                        <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] ${toneClass(cls.tone)}`}>{cls.label}</span>
                      </td>
                      <td className="px-2 py-1 text-right">
                        {row.edited ? (
                          <button
                            type="button"
                            onClick={() => resetRow(row.date)}
                            disabled={isLocked}
                            className="text-red-400 hover:text-red-200 disabled:opacity-40"
                            title="Reset row to original"
                            data-testid={`rfi-reset-row-${row.date}`}
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        ) : (
                          <Pencil className="w-3 h-3 text-slate-600" />
                        )}
                      </td>
                    </tr>
                  );
                })}
                {displayRows.length === 0 && (
                  <tr><td colSpan={14} className="text-center text-slate-500 py-3">{tableRows.length === 0 ? 'No daily rows available.' : 'No rows match the current column filters.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Bulk apply confirmation */}
      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply to remaining days?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set the adjusted recommended feed for every row from the first date onward, using <span className="font-semibold">{bulkMode === 'percent' ? `${bulkValue || 0}%` : `${bulkValue || 0} kg/day`}</span> applied to each day's original. Manually-edited rows are also overwritten. You can Reset All afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rfi-bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkApply} data-testid="rfi-bulk-confirm">Apply</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset all confirmation */}
      <AlertDialog open={resetAllConfirmOpen} onOpenChange={setResetAllConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all overrides?</AlertDialogTitle>
            <AlertDialogDescription>
              Clears every adjusted-recommended value back to the original model recommendation. The scenario will be disabled until you re-edit a row.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rfi-reset-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { resetAll(); setResetAllConfirmOpen(false); }} data-testid="rfi-reset-all-confirm">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Load scenarios dialog */}
      <AlertDialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Load scenario</AlertDialogTitle>
            <AlertDialogDescription>
              {savedScenarios.length === 0 ? 'No saved scenarios for this record yet.' : 'Pick a scenario to load. Published scenarios are read-only — clone first to edit.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-72 overflow-auto space-y-1.5" data-testid="rfi-load-list">
            {savedScenarios.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 border border-slate-700 rounded px-2 py-1.5 text-xs">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-slate-100 font-medium">{s.name}</div>
                  <div className="text-slate-400 text-[10px]">
                    {s.status} · {s.overrides.length} day{s.overrides.length === 1 ? '' : 's'} · {s.created_by_email || 'admin'} · {new Date(s.updated_at).toLocaleString()}
                  </div>
                </div>
                <Button size="sm" className="h-6 text-xs" onClick={async () => { await loadScenario(s.id); setLoadDialogOpen(false); }} data-testid={`rfi-load-${s.id}`}>Load</Button>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit log dialog */}
      <AlertDialog open={auditOpen} onOpenChange={setAuditOpen}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Immutable audit log</AlertDialogTitle>
            <AlertDialogDescription>
              Every edit, publish, archive and clone is recorded server-side and cannot be modified — even after a scenario is deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-80 overflow-auto" data-testid="rfi-audit-list">
            <table className="w-full text-xs">
              <thead className="bg-slate-800 text-slate-300">
                <tr>
                  <th className="px-2 py-1 text-left">When</th>
                  <th className="px-2 py-1 text-left">Who</th>
                  <th className="px-2 py-1 text-left">Action</th>
                  <th className="px-2 py-1 text-left">Date</th>
                  <th className="px-2 py-1 text-right">Original</th>
                  <th className="px-2 py-1 text-right">Adjusted</th>
                </tr>
              </thead>
              <tbody>
                {auditEntries.map((a) => (
                  <tr key={a.id} className="border-t border-slate-800">
                    <td className="px-2 py-1 text-slate-300 whitespace-nowrap">{new Date(a.timestamp).toLocaleString()}</td>
                    <td className="px-2 py-1 text-slate-300">{a.user_email || a.user_id}</td>
                    <td className="px-2 py-1 text-slate-300">{a.action}</td>
                    <td className="px-2 py-1 text-slate-300">{a.date || '—'}</td>
                    <td className="px-2 py-1 text-right text-slate-400">{fmtKg(a.original_recommended_kg)}</td>
                    <td className="px-2 py-1 text-right text-amber-200">{fmtKg(a.adjusted_recommended_kg)}</td>
                  </tr>
                ))}
                {auditEntries.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-500 py-3">No audit entries yet — save a scenario first.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
